/**
 * Save a project conversation and its recommended tasks, then approve them
 * into ordinary project tasks. Analysis never creates a task by itself.
 */
import { supabase } from "../supabase";
import { assertCan, CAPABILITY } from "../helpers/permissions";
import { resolveOrganizationIdForQueries } from "../helpers/orgQueryScope";
import { shouldSkipSupabase } from "../helpers/guestMode";
import { isOffline } from "../helpers/connectivity";
import { createProjectTask } from "../helpers/tasks/projectTasks";
import { analyzeConversation, toProjectTaskInput } from "./analyzeConversation";

const OFFLINE = "Reconnect to save a conversation. Recommendations are not stored on this device.";

function requireOnline() {
  if (isOffline()) throw new Error(OFFLINE);
}

async function requireOrg(actor) {
  const organizationId = await resolveOrganizationIdForQueries(actor, supabase);
  if (!organizationId) throw new Error("Organization is required.");
  return organizationId;
}

export async function loadConversationMembers(actor) {
  if (shouldSkipSupabase(actor)) return [];
  const organizationId = await requireOrg(actor);
  const { data: members, error } = await supabase
    .from("organization_members")
    .select("profile_id, employee_name")
    .eq("organization_id", organizationId)
    .eq("active", true);
  if (error) throw error;
  const profileIds = (members || []).map((row) => row.profile_id).filter(Boolean);
  if (!profileIds.length) return [];
  const { data: profiles, error: profileError } = await supabase
    .from("profiles")
    .select("id, supabase_user_id, display_name")
    .in("id", profileIds);
  if (profileError) throw profileError;
  const byProfile = new Map((profiles || []).map((row) => [row.id, row]));
  return (members || [])
    .map((member) => {
      const profile = byProfile.get(member.profile_id);
      if (!profile?.supabase_user_id) return null;
      return {
        employee_name: member.employee_name || profile.display_name || "",
        supabase_user_id: profile.supabase_user_id,
      };
    })
    .filter(Boolean);
}

export function isConversationsUnavailableError(error) {
  const text = `${error?.code || ""} ${error?.message || ""}`;
  return (
    /PGRST205|42P01/.test(text) ||
    (/project_conversation/.test(text) && /(does not exist|schema cache)/i.test(text))
  );
}

/**
 * Record the transcript, analyze it, and store proposed tasks. Returns the
 * proposals. Does not insert project_tasks.
 */
export async function captureConversation(actor, {
  projectId,
  clientId = null,
  transcript,
  locations = [],
  referenceDate,
  captureSource = "external",
}) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_VIEW);
  requireOnline();
  if (!actor?.supabase_user_id) throw new Error("Sign in before recording a conversation.");
  const text = String(transcript || "").trim();
  if (!text) throw new Error("Enter or capture a transcript before analyzing.");
  const organizationId = await requireOrg(actor);
  const members = await loadConversationMembers(actor).catch(() => []);
  const analysis = analyzeConversation(text, { referenceDate, members, locations });

  const { data: conversation, error } = await supabase
    .from("project_conversations")
    .insert({
      organization_id: organizationId,
      project_id: projectId,
      client_id: clientId,
      capture_source: captureSource === "microphone" ? "microphone" : "external",
      status: "ready",
      title: "Project conversation",
      recorded_by: actor.supabase_user_id,
      started_at: new Date().toISOString(),
      ended_at: new Date().toISOString(),
      consent_acknowledged_at: new Date().toISOString(),
    })
    .select("conversation_id")
    .single();
  if (error) throw error;

  const { error: transcriptError } = await supabase.from("project_conversation_transcripts").insert({
    organization_id: organizationId,
    conversation_id: conversation.conversation_id,
    provider: "crew_transcript",
    full_text: text,
    segments: [],
    provider_metadata: { source: "project_conversation_v1" },
  });
  if (transcriptError) throw transcriptError;

  if (!analysis.recommendations.length) {
    return { conversationId: conversation.conversation_id, recommendations: [], notes: analysis.notes };
  }

  const rows = analysis.recommendations.map((item) => ({
    organization_id: organizationId,
    conversation_id: conversation.conversation_id,
    project_id: projectId,
    title: item.title,
    description: item.description,
    location_id: item.locationId,
    assignee_id: item.assigneeId,
    due_date: item.dueDate,
    priority: item.priority,
    evidence: item.evidence,
    inference_notes: item.inferenceNotes,
    status: "proposed",
  }));
  const { data: saved, error: saveError } = await supabase
    .from("project_conversation_recommended_tasks")
    .insert(rows)
    .select("*");
  if (saveError) throw saveError;

  return {
    conversationId: conversation.conversation_id,
    recommendations: saved || [],
    notes: analysis.notes,
  };
}

export async function loadRecommendedTasks(actor, projectId) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_VIEW);
  if (shouldSkipSupabase(actor) || !projectId) return [];
  const organizationId = await requireOrg(actor);
  const { data, error } = await supabase
    .from("project_conversation_recommended_tasks")
    .select("*")
    .eq("organization_id", organizationId)
    .eq("project_id", projectId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return data || [];
}

async function markRecommendation(actor, recommendation, patch) {
  const { data, error } = await supabase
    .from("project_conversation_recommended_tasks")
    .update({
      ...patch,
      reviewed_by: actor?.supabase_user_id || null,
      reviewed_at: new Date().toISOString(),
    })
    .eq("recommendation_id", recommendation.recommendation_id)
    .select("*")
    .single();
  if (error) throw error;
  return data;
}

function rowToRecommendation(row) {
  return {
    title: row.title,
    description: row.description || "",
    assigneeId: row.assignee_id,
    dueDate: row.due_date,
    locationId: row.location_id,
    priority: row.priority,
  };
}

/** Approve one proposal into a normal project task. */
export async function approveRecommendation(actor, row, edits = {}) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_MANAGE);
  requireOnline();
  if (row.status !== "proposed") throw new Error("That recommendation was already reviewed.");
  const input = toProjectTaskInput(rowToRecommendation(row), edits);
  const task = await createProjectTask(actor, row.project_id, input);
  const updated = await markRecommendation(actor, row, {
    status: "approved",
    project_task_id: task.project_task_id,
    title: input.title,
    description: input.description || null,
    location_id: input.locationId || null,
    assignee_id: input.assigneeId || null,
    due_date: input.dueDate || null,
    priority: input.priority || null,
  });
  return { task, recommendation: updated };
}

export async function dismissRecommendation(actor, row) {
  assertCan(actor, CAPABILITY.PROJECT_TASK_MANAGE);
  requireOnline();
  if (row.status !== "proposed") throw new Error("That recommendation was already reviewed.");
  return markRecommendation(actor, row, { status: "dismissed" });
}

export async function approveAllRecommendations(actor, rows) {
  const proposed = (rows || []).filter((row) => row.status === "proposed");
  const approved = [];
  for (const row of proposed) {
    approved.push(await approveRecommendation(actor, row));
  }
  return approved;
}
